/*
 * df-motion.js — aplica la capa de movimiento sin etiquetar el marcado.
 *
 * Recorre las secciones y marca lo que debe revelarse al entrar en pantalla,
 * con un escalonado corto dentro de cada rejilla. No toca el hero: la primera
 * pantalla tiene que estar ahí cuando cargas, no aparecer.
 *
 * Coste: un IntersectionObserver que deja de observar cada elemento en cuanto
 * se revela, y un listener de scroll pasivo para la cabecera.
 */
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* requestAnimationFrame hay que llamarlo atado a window: sacado suelto y en
     modo estricto lanza "Illegal invocation" y se lleva por delante el arranque. */
  var raf = window.requestAnimationFrame
    ? window.requestAnimationFrame.bind(window)
    : function (cb) { return setTimeout(cb, 16); };

  /* Bloques que NO se revelan: o son chrome, o están sobre la línea de flotación. */
  var SKIP = [
    '.df-header', '.df-ship-bar', '.df-utility-bar',
    '.df-hero-ov', '.df-hero-red', '.df-cdrawer', '.df-pdp__stickycta',
    /* La barra de pago del carrito se mueve sola con `.is-on`. Al revelarla,
       `[data-df-m].is-in { transform: none }` —que es (0,2,1)— le ganaba a
       `.df-cart__sticky` —(0,1,0)— y la dejaba fija en pantalla: el sticky no
       se escondía nunca aunque el botón real estuviera a la vista. */
    '.df-cart__sticky',
    '.df-mobile-drawer', '.df-search-modal'
  ].join(',');

  function isSkipped(el) {
    try { return !!(el.closest(SKIP) || el.matches(SKIP)); } catch (e) { return false; }
  }

  function tag(el, delay) {
    if (!el || el.hasAttribute('data-df-m')) return;
    /* El tema ya trae su propio sistema (.df-enter / .df-anim-*). Donde ya hay
       animación no se pone otra encima: se pisan y el bloque parpadea. */
    if (el.className && /df-anim-|df-enter/.test(el.className.toString())) return;
    el.setAttribute('data-df-m', '');
    if (delay) el.style.setProperty('--df-m-delay', delay + 'ms');
  }

  /*
   * Qué se revela: los hijos directos del contenedor de cada sección. Así el
   * bloque entra como una unidad (titular + contenido) en vez de desmontarse
   * en veinte trozos, que es lo que hace que una página parezca nerviosa.
   * Dentro de rejillas de tarjetas sí se escalona, porque ahí el escalonado
   * ayuda a leer el orden.
   */
  function collect() {
    var out = [];
    document.querySelectorAll('#main-content [id^="shopify-section-"]').forEach(function (sec) {
      if (isSkipped(sec)) return;
      var root = sec.querySelector('.df-container') || sec.firstElementChild || sec;
      var kids = Array.prototype.slice.call(root.children);
      if (!kids.length) { out.push(sec); return; }
      kids.forEach(function (k) {
        if (isSkipped(k)) return;
        var r = k.getBoundingClientRect();
        if (r.height < 24) return;          // separadores y cosas vacías
        out.push(k);
        // Escalonado dentro de rejillas: máximo 6, y 55ms por paso
        var grid = k.matches('[class*="grid"],[class*="__cards"],[class*="__items"]')
          ? k : k.querySelector('[class*="grid"],[class*="__cards"],[class*="__items"]');
        if (grid) {
          Array.prototype.slice.call(grid.children, 0, 6).forEach(function (c, i) {
            if (c.getBoundingClientRect().height <= 40) return;
            tag(c, i * 55);
            /* Los hijos escalonados TIENEN que entrar en la lista: si se
               marcan y no se observan, quedan ocultos para siempre. Pasó con
               el carrito entero. */
            out.push(c);
          });
        }
      });
    });
    return out;
  }

  function reveal(el) {
    el.classList.add('is-in');
    var done = function () { el.classList.add('is-done'); };
    el.addEventListener('transitionend', done, { once: true });
    setTimeout(done, 900);   // red de seguridad si no llega el transitionend
  }

  /* Red final: revela TODO lo marcado en el documento, venga de donde venga.
     Que un bloque se quede invisible es peor que no animar nada. */
  function revealAll() {
    document.querySelectorAll('[data-df-m]').forEach(function (el) {
      el.classList.add('is-in', 'is-done');
    });
  }

  function initReveal() {
    var els = collect();
    if (!els.length) return;

    if (REDUCED || !('IntersectionObserver' in window)) return;   // no se marca nada

    els.forEach(function (el) { tag(el); });
    // A partir de aquí el CSS ya puede esconder: hay JS vivo para revelarlo.
    document.documentElement.classList.add('df-motion-on');

    /*
     * Tres redes de seguridad, porque quedarse invisible es mucho peor que no
     * animar: al primer scroll se revisa todo, a los 2,5s se revela lo que
     * quede dentro de pantalla, y a los 6s se revela absolutamente todo.
     */
    var sweep = function () {
      document.querySelectorAll('[data-df-m]:not(.is-in)').forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) reveal(el);
      });
    };
    window.addEventListener('scroll', sweep, { passive: true });
    window.addEventListener('resize', sweep, { passive: true });
    /* El layout se asienta durante el primer segundo y pico (fuentes, imágenes,
       embeds). Se barre a intervalos cortos hasta que converge, y se para. */
    var ticks = 0;
    var settle = setInterval(function () {
      sweep();
      if (++ticks >= 6) clearInterval(settle);   // 6 × 250ms = 1,5s
    }, 250);
    setTimeout(revealAll, 2500);

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        reveal(e.target);
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.02 });

    els.forEach(function (el) {
      // Lo que ya está en pantalla al cargar se muestra sin animar: animar algo
      // que el usuario ya está mirando es un parpadeo, no una entrada.
      var r = el.getBoundingClientRect();
      /* Todo lo que asome en pantalla al cargar se muestra ya. Animar algo que
         el usuario está viendo es un parpadeo, y esconderlo es peor. */
      if (r.top < window.innerHeight) { el.classList.add('is-in', 'is-done'); return; }
      io.observe(el);
    });

    setTimeout(sweep, 200);
  }

  /* Cabecera: sombra solo cuando hay contenido por debajo */
  function initHeader() {
    var header = document.querySelector('.df-header');
    if (!header) return;
    var ticking = false;
    function update() {
      ticking = false;
      header.classList.toggle('is-stuck', window.scrollY > 4);
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      raf(update);
    }, { passive: true });
    update();
  }

  /* Imágenes lazy: entran cuando están decodificadas */
  function initImages() {
    if (REDUCED) return;
    document.querySelectorAll('#main-content img[loading="lazy"]').forEach(function (img) {
      if (img.closest(SKIP)) return;
      if (img.complete && img.naturalWidth) return;   // ya estaba en caché
      img.classList.add('df-m-img');
      var show = function () { img.classList.add('is-loaded'); };
      img.addEventListener('load', show, { once: true });
      img.addEventListener('error', show, { once: true });
      setTimeout(show, 2500);
    });
  }

  function boot() {
    initHeader();
    initImages();

    /*
     * El revelado se monta al 'load', no antes. Decidir qué está bajo la línea
     * de flotación mientras el layout se mueve (fuentes, imágenes, embeds) es
     * la forma segura de esconder algo que el usuario ya tiene delante: pasó
     * exactamente eso con la sección de vídeo. Al 'load' las posiciones ya son
     * las buenas, y hasta entonces no se esconde nada.
     */
    var start = function () {
      raf(initReveal);
    };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
